/**
 * Maps Local Bridge MarketFit API types to MarketFitContext types.
 * Used when loading deal detail from Local Bridge in MarketFitAnalysisPage.
 */

import type {
  ProductSolution,
  CustomerSituation,
  CustomerActor,
  ConnectionLinksData,
  ConnectionLink,
  SolutionInput,
  SolutionBenefit,
  SituationResource,
  SituationNeed,
  SituationInput,
  SituationOutput,
} from '../types/marketFit';
import type { MarketFitDealDetail, MarketFitProduct, MarketFitSituation, MarketFitActor } from './localBridgeSessionApi';
import {
  parseProductInputsBenefits,
  parseSituationDetails,
  parseDealConnectionLinks,
} from '../types/marketFitLocalBridge';

export interface MappedDealDetail {
  solution: ProductSolution | null;
  situation: CustomerSituation | null;
  actor: CustomerActor | null;
  connectionLinks: ConnectionLinksData;
  actorSituationAlignment: number;
}

function mapProductToSolution(product: MarketFitProduct): ProductSolution {
  const parsed = parseProductInputsBenefits(product.inputs_benefits_json);
  const inputs: SolutionInput[] = parsed.inputs.map((inp) => ({
    id: inp.id,
    name: inp.name,
    description: inp.description,
    isRequired: inp.isRequired ?? true,
    category: inp.category,
  }));
  const benefits: SolutionBenefit[] = parsed.benefits.map((ben) => ({
    id: ben.id,
    name: ben.name,
    description: ben.description,
    valueProposition: ben.valueProposition,
    category: ben.category,
  }));
  return {
    id: product.id,
    name: product.name,
    description: product.description ?? undefined,
    inputs,
    benefits,
    productPrice: product.product_price ?? 0,
    productCostCogs: product.product_cost_cogs ?? 0,
    productCostDevelopment: product.product_cost_development ?? 0,
  };
}

function mapSituationToCustomerSituation(situation: MarketFitSituation): CustomerSituation {
  const parsed = parseSituationDetails(situation.details_json);
  const resources: SituationResource[] = parsed.assets.map((a) => ({
    id: a.id,
    name: a.name,
    description: a.description,
    cost: a.cost ?? 0,
    isExisting: a.isExisting ?? true,
  }));
  const needs: SituationNeed[] = parsed.needs.map((n) => ({
    id: n.id,
    name: n.name,
    description: n.description,
    priority: (n.priority ?? 'medium') as 'high' | 'medium' | 'low',
  }));
  const inputs: SituationInput[] = parsed.inputs.map((i) => ({
    id: i.id,
    name: i.name,
    description: i.description,
    source: i.source,
  }));
  const outputs: SituationOutput[] = parsed.outputs.map((o) => ({
    id: o.id,
    name: o.name,
    description: o.description,
    value: o.value,
  }));
  return {
    id: situation.id,
    name: situation.name,
    situationName: situation.situation_name ?? situation.name,
    description: situation.description ?? undefined,
    marketSegment: situation.market_segment ?? '',
    customerType: situation.customer_type ?? '',
    demographics: [],
    needs,
    resources,
    inputs,
    outputs,
    valueGenerated: situation.value_generated ?? 0,
    resourceCost: situation.resource_cost ?? 0,
  };
}

function mapActorToCustomerActor(actor: MarketFitActor): CustomerActor {
  return {
    id: actor.id,
    actorName: actor.actor_name ?? '',
    actorRole: actor.actor_role ?? '',
    description: undefined,
    demographics: [],
    priorities: [],
    motivations: [],
  };
}

function mapDealConnectionLinksToContext(links: {
  solutionInputToSituationResourceLinks: Array<{ id: string; fromId: string; toId: string; matchType: string; userOverride?: boolean }>;
  solutionBenefitToSituationNeedLinks: Array<{ id: string; fromId: string; toId: string; matchType: string; userOverride?: boolean }>;
}): ConnectionLinksData {
  const toLink = (l: { id: string; fromId: string; toId: string; matchType: string; userOverride?: boolean }): ConnectionLink => ({
    id: l.id,
    fromId: l.fromId,
    toId: l.toId,
    matchType: (l.matchType === 'full' || l.matchType === 'partial' ? l.matchType : 'none') as 'full' | 'partial' | 'none',
    userOverride: l.userOverride ?? false,
  });
  return {
    solutionInputToSituationResourceLinks: links.solutionInputToSituationResourceLinks.map(toLink),
    solutionBenefitToSituationNeedLinks: links.solutionBenefitToSituationNeedLinks.map(toLink),
  };
}

/**
 * Map MarketFitDealDetail from Local Bridge API to MarketFitContext-compatible shape.
 */
export function mapMarketFitDealDetailToContext(detail: MarketFitDealDetail): MappedDealDetail {
  const product = detail.products?.[0];
  const situation = detail.situations?.[0];
  const actor = detail.actor;

  const connectionLinksRaw = parseDealConnectionLinks(detail.connection_links_json);
  const connectionLinks = mapDealConnectionLinksToContext(connectionLinksRaw);

  return {
    solution: product ? mapProductToSolution(product) : null,
    situation: situation ? mapSituationToCustomerSituation(situation) : null,
    actor: actor ? mapActorToCustomerActor(actor) : null,
    connectionLinks,
    actorSituationAlignment: detail.actor_situation_alignment ?? 0.5,
  };
}
